import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import RemoveIcon from 'assets/icons/remove-icon'
import AssessIcon from 'assets/icons/assess-icon'
import Button from 'components/Button'
import { congregationOptions } from 'constants/congregationOptions'
import { defaultSiteProposalValidationResults } from 'constants/defaultSiteProposalValidationResults'
import PulsingNotification from 'features/PulsingNotification'
import { SiteContext } from 'providers/site'
import { SITE_STATUS_DRAFT_ID } from 'constants/siteStatuses'
import Tooltip from 'components/Tooltip'
import CalendarIcon from 'assets/icons/calendar-icon'
import ConfirmIcon from 'assets/icons/confirm-icon'
import RejectIcon from 'assets/icons/reject-icon'
import DropdownIndicatorIconClosed from 'assets/icons/dropdown-indicator-closed24px'
import DropdownIndicatorIconOpen from 'assets/icons/dropdown-indicator-opened24px'
import ReassessIcon from 'assets/icons/reassess-icon'
import { getCongregationNameByGroup, getRejectReasonNames, hasQualifyingReason } from 'utils/congregationHelpers'

import {
  CongregationHeaderCalendarDateText, CongregationHeaderCalendarDivider,
  CongregationHeaderCalendarIconWrapper,
  CongregationHeaderDotIcon,
  CongregationHeaderHalfSide,
  CongregationHeaderRemoveIconWrapper,
  CongregationHeaderTitle,
  CongregationHeaderWrapper,
  CongregationsHeaderLastReassessmentCalendarWrapper,
  CongregationValidationIcon,
  CongregationValidationIconWrapper,
  CongregationConfirmDeclineIcon,
  CongregationToggleWrapper
} from './style'


const tooltipXOffset = 0
const tooltipYOffset = -16
const dateOptions = {
  year: "numeric",
  month: "short",
  weekday: "short",
  day: "numeric",
  hour: "numeric",
  minute: "numeric",
  second: "numeric",
  hour12: true // This is the line of code we care about here
  /*
      false: displays 24hs format for time
      true: displays 12, AM and PM format
  */
};

const ReassessmentDate = ({ hasDateToShow, dateToShow }) => {
  if (!hasDateToShow) {
    return null
  }

  return (
    <Tooltip
      style={{ zIndex: 1000 }}
      placement='top'
      trigger={['hover']}
      align={{ offset: [tooltipXOffset, tooltipYOffset] }}
      overlay={{ label: 'lastTimeReassessed' }}
    >
      <CongregationsHeaderLastReassessmentCalendarWrapper>
        <CongregationHeaderCalendarIconWrapper>
          <CalendarIcon />
        </CongregationHeaderCalendarIconWrapper>
        <CongregationHeaderCalendarDateText>
          {dateToShow.toLocaleDateString('en-US', dateOptions)}
        </CongregationHeaderCalendarDateText>
        <CongregationHeaderCalendarDivider />
      </CongregationsHeaderLastReassessmentCalendarWrapper>
    </Tooltip>
  )
}

const QualificationIndicator = ({ qualifies, formattedRejectIconTooltipHint }) => {
  return (
    <CongregationConfirmDeclineIcon type={qualifies ? 'confirmed' : 'rejected'}>
      {qualifies ? (
        <Tooltip
          style={{ zIndex: 1000 }}
          placement='top'
          trigger={['hover']}
          align={{ offset: [tooltipXOffset, tooltipYOffset] }}
          overlay={{ label: 'qualifies' }}
        >
          <div>
            <ConfirmIcon />
          </div>
        </Tooltip>
      ) : (
        <Tooltip
          style={{ zIndex: 1000 }}
          placement='top'
          trigger={['hover']}
          align={{ offset: [tooltipXOffset, tooltipYOffset] }}
          overlay={{ label: 'nonQualifying', infoTooltip: formattedRejectIconTooltipHint.join(', ') }}
        >
          <div>
            <RejectIcon />
          </div>
        </Tooltip>
      )}
    </CongregationConfirmDeclineIcon>
  )
}

const RemoveCongregationButton = ({ item, siteStatusId, disabled, openRemoveModal, isReassessmentMode }) => {
  const canRenderRemove = siteStatusId === SITE_STATUS_DRAFT_ID && !disabled
  if (!canRenderRemove) {
    return null
  }

  if (isReassessmentMode && item.lastReassessmentDate) {
    return null
  }

  return (
    <CongregationHeaderRemoveIconWrapper
      delete={isReassessmentMode}
      data-testid='remove-button'
      onClick={(e) => {
        e.stopPropagation()
        openRemoveModal(item)
      }}
    >
      <RemoveIcon />
    </CongregationHeaderRemoveIconWrapper>
  )
}

const HeaderActions = ({
  isOpen,
  item,
  toggleAccordion,
  openRemoveModal,
  site,
  oldTriggers,
  newTriggers,
  disabled,
  fieldsToCompleteNumber,
  hasDateToShow,
  dateToShow,
  qualifies,
  formattedRejectIconTooltipHint
}) => {
  const isDraft = site.SiteStatusId === SITE_STATUS_DRAFT_ID
  const actionMessageId = oldTriggers ? 'reassess' : 'assess'

  const renderValidationIndicator = () => {
    if (fieldsToCompleteNumber.length === 0) {
      return null
    }

    return (
      <CongregationValidationIconWrapper>
        <CongregationValidationIcon>
          <PulsingNotification number={fieldsToCompleteNumber.length} />
        </CongregationValidationIcon>
      </CongregationValidationIconWrapper>
    )
  }

  const renderDraftActionButton = (IconComponent) => (
    <Button
      isDisabled={isOpen}
      type={isOpen ? 'defaultDisabled' : ''}
      height='32px'
      marginTop='0'
      onClick={toggleAccordion}
    >
      <IconComponent />
      <FormattedMessage id={actionMessageId} />
    </Button>
  )

  const renderReadOnlyActions = () => (
    <>
      <ReassessmentDate hasDateToShow={hasDateToShow} dateToShow={dateToShow} />
      <QualificationIndicator
        qualifies={qualifies}
        formattedRejectIconTooltipHint={formattedRejectIconTooltipHint}
      />
      <CongregationToggleWrapper isActive={isOpen} onClick={toggleAccordion}>
        <FormattedMessage id='expand' />
        {isOpen ? <DropdownIndicatorIconOpen /> : <DropdownIndicatorIconClosed />}
      </CongregationToggleWrapper>
    </>
  )

  const renderReassessmentActions = () => (
    <>
      <ReassessmentDate hasDateToShow={hasDateToShow} dateToShow={dateToShow} />
      <QualificationIndicator
        qualifies={qualifies}
        formattedRejectIconTooltipHint={formattedRejectIconTooltipHint}
      />
      {isDraft ? renderDraftActionButton(ReassessIcon) : renderReadOnlyActions()}
      <RemoveCongregationButton
        item={item}
        siteStatusId={site.SiteStatusId}
        disabled={disabled}
        openRemoveModal={openRemoveModal}
        isReassessmentMode={true}
      />
    </>
  )

  const renderStandardActions = () => (
    <>
      {isDraft ? renderDraftActionButton(AssessIcon) : renderReadOnlyActions()}
      <RemoveCongregationButton
        item={item}
        siteStatusId={site.SiteStatusId}
        disabled={disabled}
        openRemoveModal={openRemoveModal}
        isReassessmentMode={false}
      />
    </>
  )

  const shouldUseReassessmentActions = site.reassessmentFlag || oldTriggers || newTriggers

  return (
    <>
      {shouldUseReassessmentActions ? renderReassessmentActions() : renderStandardActions()}
      {renderValidationIndicator()}
    </>
  )
}

const CongregationHeader = ({ isOpen, item, toggleAccordion, lastItem, openRemoveModal, site, newTriggers = false, oldTriggers = false }) => {
  const { siteProposalFieldValidation, isEditable } = useContext(SiteContext)

  const dateToShow = item.updatedAt ? new Date(item.updatedAt) : new Date()
  const hasDateToShow = Boolean(item.updatedAt)
  const name = getCongregationNameByGroup(item.CongregationGroupId, congregationOptions)

  const validationCongregation = siteProposalFieldValidation?.congregations?.[item.id] || defaultSiteProposalValidationResults.congregations

  const fieldsToCompleteNumber = Object.values(validationCongregation).filter((element) => {
    return element === false
  })

  const formattedRejectIconTooltipHint = getRejectReasonNames(item.trigger_reason_id)
  const qualifies = hasQualifyingReason(item.trigger_reason_id)

  const disabled = site.SiteStatusId !== SITE_STATUS_DRAFT_ID || (!isEditable && site.SiteStatusId === SITE_STATUS_DRAFT_ID) || !qualifies

  return (
    <CongregationHeaderWrapper borderBottom={isOpen || lastItem} marginBottom={isOpen} data-testId='congregation-header'>
        <CongregationHeaderHalfSide onClick={toggleAccordion}>
          <CongregationHeaderDotIcon isRejected={!qualifies} />
          <CongregationHeaderTitle isRejected={!qualifies} isReassess={item.lastReassessmentDate}>
            <FormattedMessage id={name} />
          </CongregationHeaderTitle>
        </CongregationHeaderHalfSide>
        <CongregationHeaderHalfSide>
          <HeaderActions
            isOpen={isOpen}
            item={item}
            toggleAccordion={toggleAccordion}
            openRemoveModal={openRemoveModal}
            site={site}
            oldTriggers={oldTriggers}
            newTriggers={newTriggers}
            disabled={disabled}
            fieldsToCompleteNumber={fieldsToCompleteNumber}
            hasDateToShow={hasDateToShow}
            dateToShow={dateToShow}
            qualifies={qualifies}
            formattedRejectIconTooltipHint={formattedRejectIconTooltipHint}
          />
        </CongregationHeaderHalfSide>
      </CongregationHeaderWrapper>
  )
}

export default CongregationHeader

const congregationItemPropType = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  CongregationGroupId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  trigger_reason_id: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  updatedAt: PropTypes.string,
  lastReassessmentDate: PropTypes.string
})

ReassessmentDate.propTypes = {
  hasDateToShow: PropTypes.bool.isRequired,
  dateToShow: PropTypes.instanceOf(Date).isRequired
}

QualificationIndicator.propTypes = {
  qualifies: PropTypes.bool.isRequired,
  formattedRejectIconTooltipHint: PropTypes.arrayOf(PropTypes.string).isRequired
}

RemoveCongregationButton.propTypes = {
  item: congregationItemPropType.isRequired,
  siteStatusId: PropTypes.number.isRequired,
  disabled: PropTypes.bool.isRequired,
  openRemoveModal: PropTypes.func.isRequired,
  isReassessmentMode: PropTypes.bool.isRequired
}

HeaderActions.propTypes = {
  isOpen: PropTypes.bool,
  item: congregationItemPropType.isRequired,
  toggleAccordion: PropTypes.func.isRequired,
  openRemoveModal: PropTypes.func.isRequired,
  site: PropTypes.shape({
    SiteStatusId: PropTypes.number.isRequired,
    reassessmentFlag: PropTypes.bool
  }).isRequired,
  oldTriggers: PropTypes.bool,
  newTriggers: PropTypes.bool,
  disabled: PropTypes.bool.isRequired,
  fieldsToCompleteNumber: PropTypes.arrayOf(PropTypes.bool).isRequired,
  hasDateToShow: PropTypes.bool.isRequired,
  dateToShow: PropTypes.instanceOf(Date).isRequired,
  qualifies: PropTypes.bool.isRequired,
  formattedRejectIconTooltipHint: PropTypes.arrayOf(PropTypes.string).isRequired
}

CongregationHeader.propTypes = {
  isOpen: PropTypes.bool,
  item: congregationItemPropType.isRequired,
  toggleAccordion: PropTypes.func.isRequired,
  lastItem: PropTypes.bool,
  openRemoveModal: PropTypes.func.isRequired,
  site: PropTypes.shape({
    SiteStatusId: PropTypes.number.isRequired,
    reassessmentFlag: PropTypes.bool
  }).isRequired,
  newTriggers: PropTypes.bool,
  oldTriggers: PropTypes.bool
}
